import { OrgRole, type OrganizationMembership } from 'pure-js/types/OrganizationMembership'
import { isOrgRole, isStaffOrgRole } from 'pure-js/libs/organizationMembershipHelpers'
import type { User } from 'pure-js/types/User'
import { GamerRoutes, FacilitatorRoutes } from '../../config/routes/routeEnums'
import { buildOrgOnboardingPath } from './orgOnboardingNavigation'

/**
 * Sync local user state with a successful `acceptOrgInvite` response so route
 * guards see the new tenant before Firestore listeners catch up.
 */
export function applyAcceptedOrgInviteToUser(user: User, accept: { organizationId: string; role: string }): User {
  const { organizationId, role } = accept
  const organizations = [...(user.organizations ?? [])]
  const existingIndex = organizations.findIndex((m) => m.organizationId === organizationId)
  const membership: OrganizationMembership = {
    organizationId,
    role: role as OrgRole,
    joinedAt: existingIndex >= 0 ? organizations[existingIndex].joinedAt : new Date().toISOString()
  }
  if (existingIndex >= 0) {
    organizations[existingIndex] = { ...organizations[existingIndex], ...membership }
  } else {
    organizations.push(membership)
  }
  const organizationIds = Array.from(new Set([...(user.organizationIds ?? []), organizationId]))
  return {
    ...user,
    organizations,
    organizationIds,
    activeOrganizationId: organizationId
  }
}

export function getOrgAreaForRole(role?: string): 'gamer' | 'facilitator' {
  const isStaffRole = isOrgRole(role) && isStaffOrgRole(role)
  return isStaffRole ? 'facilitator' : 'gamer'
}

export function getAcceptedInviteTarget(
  responseData: { organizationId?: string; role?: string } | undefined,
  fallbackOrgId: string | undefined,
  options?: { isPendingOnboarding?: boolean }
): { organizationId: string; area: 'gamer' | 'facilitator' | 'onboarding' } | null {
  const organizationId = responseData?.organizationId || fallbackOrgId || ''
  if (!organizationId) return null
  // PENDING_ONBOARDING org + ADMIN role → run the onboarding wizard before the
  // facilitator/gamer shells. The wizard lives at `/org/:orgId/onboarding` so
  // it cannot collide with `/gamer/*` or `/facilitator/*`.
  // Other accept flows skip onboarding regardless of org status (only the org
  // admin can finish onboarding).
  if (options?.isPendingOnboarding && responseData?.role === OrgRole.ADMIN) {
    return { organizationId, area: 'onboarding' }
  }
  return { organizationId, area: getOrgAreaForRole(responseData?.role) }
}

export type AcceptedInviteTarget = NonNullable<ReturnType<typeof getAcceptedInviteTarget>>

/**
 * Maps accept-invite routing target to a concrete app path (current App.tsx routes).
 */
export function buildAcceptedInviteDestination(target: AcceptedInviteTarget): string {
  if (target.area === 'onboarding') {
    return buildOrgOnboardingPath(target.organizationId)
  }
  if (target.area === 'facilitator') {
    return FacilitatorRoutes.BASE
  }
  return GamerRoutes.BASE
}

/**
 * After acceptOrgInvite, decide if the org admin must complete onboarding.
 */
export function resolveIsPendingOnboardingAfterAccept(options: {
  role?: string
  isOnboardingHint: boolean
  organizationStatus?: string
  orgFetchFailed?: boolean
}): boolean {
  if (options.role !== OrgRole.ADMIN) return false
  if (options.organizationStatus === 'PENDING_ONBOARDING') return true
  if (options.isOnboardingHint && options.orgFetchFailed) return true
  if (options.isOnboardingHint && !options.organizationStatus) return true
  return false
}

/**
 * Raw error codes the backend (`OrgInvitationHandler`) can return for the invite
 * accept flow, plus two FE-synthesized codes (`MISSING_TOKEN`, `UNKNOWN`).
 * Strings match the `HttpsError` message — do not rename without updating the
 * backend.
 */
export enum AcceptInviteErrorCode {
  EMAIL_MISMATCH = 'invite-email-mismatch',
  EXPIRED = 'invite-expired',
  USED = 'invite-not-found-or-used',
  CAPACITY = 'organization-at-capacity',
  INVALID_LINK = 'invalid-link',
  UNKNOWN = 'unknown'
}

/** Visual tone for an invite error. Drives icon, accent color, and surrounding chrome. */
export enum AcceptInviteErrorTone {
  /** User can recover with a small action (switch account, wait for new invite). Amber accent. */
  SOFT = 'soft',
  /** Something genuinely broken (capacity exhausted, malformed link, unknown). Red accent. */
  HARD = 'hard'
}

/** Symbolic icon name resolved to a Heroicon component in the page. */
export enum AcceptInviteErrorIcon {
  MAIL_MISMATCH = 'mailMismatch',
  EXPIRED = 'expired',
  USED = 'used',
  CAPACITY = 'capacity',
  INVALID_LINK = 'invalidLink',
  GENERIC = 'generic'
}

/** Primary recovery action to offer for an error. */
export enum AcceptInvitePrimaryAction {
  /** Navigate to the role-appropriate landing route. */
  GO_HOME = 'goHome',
  /** Sign out + go to /login so the user can authenticate with the matching email. */
  SWITCH_ACCOUNT = 'switchAccount'
}

export interface AcceptInviteErrorView {
  code: AcceptInviteErrorCode
  tone: AcceptInviteErrorTone
  icon: AcceptInviteErrorIcon
  titleKey: string
  descriptionKey: string
  primaryAction: AcceptInvitePrimaryAction
}

/**
 * Firebase v2 callable errors arrive as `Error` instances whose `.message` is the
 * `HttpsError` message (e.g. `"invite-email-mismatch"`). We pattern-match the raw
 * string here so the page can render a tailored card per scenario. Forgiving
 * about prefixes since Firebase sometimes wraps messages.
 */
export function classifyAcceptInviteError(error: string | undefined): AcceptInviteErrorView {
  const raw = (error || '').trim()
  const has = (needle: string) => raw.includes(needle)

  if (has(AcceptInviteErrorCode.EMAIL_MISMATCH)) {
    return {
      code: AcceptInviteErrorCode.EMAIL_MISMATCH,
      tone: AcceptInviteErrorTone.SOFT,
      icon: AcceptInviteErrorIcon.MAIL_MISMATCH,
      titleKey: 'org.invite.errors.emailMismatchTitle',
      descriptionKey: 'org.invite.errors.emailMismatchDescription',
      primaryAction: AcceptInvitePrimaryAction.SWITCH_ACCOUNT
    }
  }
  if (has(AcceptInviteErrorCode.EXPIRED)) {
    return {
      code: AcceptInviteErrorCode.EXPIRED,
      tone: AcceptInviteErrorTone.SOFT,
      icon: AcceptInviteErrorIcon.EXPIRED,
      titleKey: 'org.invite.errors.expiredTitle',
      descriptionKey: 'org.invite.errors.expiredDescription',
      primaryAction: AcceptInvitePrimaryAction.GO_HOME
    }
  }
  if (has(AcceptInviteErrorCode.USED)) {
    return {
      code: AcceptInviteErrorCode.USED,
      tone: AcceptInviteErrorTone.SOFT,
      icon: AcceptInviteErrorIcon.USED,
      titleKey: 'org.invite.errors.usedTitle',
      descriptionKey: 'org.invite.errors.usedDescription',
      primaryAction: AcceptInvitePrimaryAction.GO_HOME
    }
  }
  if (has('member limit')) {
    return {
      code: AcceptInviteErrorCode.CAPACITY,
      tone: AcceptInviteErrorTone.HARD,
      icon: AcceptInviteErrorIcon.CAPACITY,
      titleKey: 'org.invite.errors.capacityTitle',
      descriptionKey: 'org.invite.errors.capacityDescription',
      primaryAction: AcceptInvitePrimaryAction.GO_HOME
    }
  }
  if (has('missing-token') || has('inviteToken-required')) {
    return {
      code: AcceptInviteErrorCode.INVALID_LINK,
      tone: AcceptInviteErrorTone.HARD,
      icon: AcceptInviteErrorIcon.INVALID_LINK,
      titleKey: 'org.invite.errors.invalidLinkTitle',
      descriptionKey: 'org.invite.errors.invalidLinkDescription',
      primaryAction: AcceptInvitePrimaryAction.GO_HOME
    }
  }
  return {
    code: AcceptInviteErrorCode.UNKNOWN,
    tone: AcceptInviteErrorTone.HARD,
    icon: AcceptInviteErrorIcon.GENERIC,
    titleKey: 'org.invite.errors.genericTitle',
    descriptionKey: 'org.invite.errors.genericDescription',
    primaryAction: AcceptInvitePrimaryAction.GO_HOME
  }
}
